import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { Book, Zap, ChevronRight, Award, Network } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { DonationSection } from '@/components/DonationSection';

const LearningPaths = () => {
  useSeoMeta({
    title: 'Rutas de Aprendizaje - Bitcoin Life Panamá',
    description: 'Cursos estructurados diseñados para llevarte de principiante a experto en Bitcoin. Tres módulos completos con enfoque práctico.',
  });

  const learningPaths = [
    {
      id: 'beginner',
      title: 'Módulo 1: Fundamentos',
      description: 'Historia, filosofía y conceptos básicos de Bitcoin',
      icon: Book,
      lessons: 15,
      duration: '6 horas',
      color: 'from-orange-500 to-amber-500',
      topics: ['Historia del Dinero', 'Qué es Bitcoin', 'Blockchain Básico', 'Criptografía'],
      detailedDescription: 'Comienza tu viaje en Bitcoin con una base sólida. Este módulo cubre la evolución del dinero desde el trueque hasta Bitcoin, la filosofía detrás del dinero descentralizado, y los conceptos fundamentales que necesitas para entender por qué Bitcoin es importante.',
      learningOutcomes: [
        'Comprender la historia y evolución del dinero',
        'Explicar qué es Bitcoin y cómo funciona',
        'Entender los principios básicos de blockchain',
        'Conocer los fundamentos de criptografía aplicada a Bitcoin',
        'Diferenciar Bitcoin de otras criptomonedas',
        'Identificar casos de uso reales de Bitcoin'
      ],
      prerequisites: 'Ninguno - Ideal para principiantes sin conocimientos previos'
    },
    {
      id: 'intermediate',
      title: 'Módulo 2: Tecnología',
      description: 'Aspectos técnicos y funcionamiento de la red Bitcoin',
      icon: Network,
      lessons: 20,
      duration: '12 horas',
      color: 'from-blue-500 to-cyan-500',
      topics: ['Minería', 'Nodos', 'Transacciones', 'Scripts', 'Seguridad'],
      detailedDescription: 'Profundiza en los aspectos técnicos de Bitcoin. Aprende cómo funciona la minería, qué es un nodo, cómo se procesan las transacciones, y las características de seguridad que hacen de Bitcoin el dinero más seguro del mundo.',
      learningOutcomes: [
        'Entender el proceso completo de minería y Prueba de Trabajo',
        'Configurar y operar un nodo Bitcoin',
        'Analizar la estructura de transacciones Bitcoin',
        'Comprender scripts y smart contracts básicos',
        'Implementar mejores prácticas de seguridad',
        'Diagnosticar problemas comunes en la red'
      ],
      prerequisites: 'Módulo 1 completado o conocimientos básicos de Bitcoin'
    },
    {
      id: 'advanced',
      title: 'Módulo 3: Aplicaciones',
      description: 'Lightning Network, desarrollo y economía circular',
      icon: Award,
      lessons: 25,
      duration: '18 horas',
      color: 'from-purple-500 to-pink-500',
      topics: ['Lightning Network', 'Desarrollo', 'Comercio', 'Adopción en Panamá'],
      detailedDescription: 'El módulo más completo sobre aplicaciones prácticas de Bitcoin. Domina Lightning Network para pagos instantáneos, aprende a desarrollar sobre Bitcoin, y descubre cómo contribuir a la economía circular en Ciudad de Panamá.',
      learningOutcomes: [
        'Dominar Lightning Network para pagos instantáneos',
        'Configurar billeteras y canales Lightning',
        'Desarrollar aplicaciones sobre Bitcoin',
        'Implementar pagos Bitcoin en comercios',
        'Participar activamente en la economía circular local',
        'Contribuir al ecosistema Bitcoin en Panamá'
      ],
      prerequisites: 'Módulos 1 y 2 completados o experiencia técnica con Bitcoin'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-900">
      <Header />

      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-orange-100 via-amber-50 to-yellow-100 dark:from-orange-950/30 dark:via-amber-950/20 dark:to-yellow-950/30 py-20">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTEsMTkxLDM2LDAuMSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-40"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge className="px-4 py-1.5 text-sm bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300 border-orange-200 dark:border-orange-800">
              <Book className="w-3.5 h-3.5 mr-1.5 inline" />
              Cursos Estructurados
            </Badge>
            
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-white">
              Rutas de Aprendizaje
            </h1>
            
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Tres módulos progresivos diseñados para llevarte de principiante a experto en Bitcoin.
              Cada ruta incluye lecciones estructuradas, ejercicios prácticos y certificación.
            </p>
          </div>
        </div>
      </div>

      {/* Learning Paths */}
      <div className="container mx-auto px-4 py-20">
        <div className="max-w-6xl mx-auto space-y-12">
          {learningPaths.map((path, index) => {
            const Icon = path.icon;
            return (
              <Card
                key={path.id}
                className="group hover:shadow-2xl transition-all duration-500 border-2 hover:border-orange-200 dark:hover:border-orange-800 overflow-hidden"
              >
                <div className={`h-2 bg-gradient-to-r ${path.color}`}></div>
                
                <div className="grid md:grid-cols-3 gap-8 p-8">
                  {/* Left: Icon and Overview */}
                  <div className="space-y-6">
                    <div className={`w-20 h-20 rounded-3xl bg-gradient-to-br ${path.color} flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-xl`}>
                      <Icon className="w-10 h-10 text-white" />
                    </div>
                    
                    <div>
                      <div className="text-sm font-semibold text-muted-foreground mb-2">
                        Módulo {index + 1} de 3
                      </div>
                      <h2 className="text-3xl font-bold mb-3 text-gray-900 dark:text-white group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">
                        {path.title}
                      </h2>
                      <p className="text-base text-gray-600 dark:text-gray-400 mb-4">
                        {path.description}
                      </p>

                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Book className="w-4 h-4 text-orange-500" />
                          <span>{path.lessons} lecciones</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Zap className="w-4 h-4 text-orange-500" />
                          <span>{path.duration} de contenido</span>
                        </div>
                      </div>
                    </div>

                    <Link to={`/lessons/${path.id}`} className="block">
                      <Button 
                        className={`w-full bg-gradient-to-r ${path.color} hover:opacity-90 text-white h-12 text-base font-semibold group/btn`}
                      >
                        Comenzar Curso
                        <ChevronRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </div>

                  {/* Right: Details */}
                  <div className="md:col-span-2 space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
                        Descripción del Módulo
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                        {path.detailedDescription}
                      </p>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
                        Lo que Aprenderás
                      </h3>
                      <div className="grid md:grid-cols-2 gap-3">
                        {path.learningOutcomes.map((outcome, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <ChevronRight className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {outcome}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
                        Temas Principales
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {path.topics.map((topic, idx) => (
                          <Badge key={idx} variant="secondary" className="text-sm px-3 py-1">
                            {topic}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-start gap-2 text-sm">
                        <span className="font-semibold text-gray-700 dark:text-gray-300">
                          Requisitos:
                        </span>
                        <span className="text-gray-600 dark:text-gray-400">
                          {path.prerequisites}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Additional Info */}
        <div className="mt-16">
          <Card className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20 border-2 border-orange-200 dark:border-orange-800">
            <CardContent className="py-10 px-8">
              <div className="max-w-4xl mx-auto text-center space-y-6">
                <h3 className="text-3xl font-bold text-gray-900 dark:text-white">
                  Programa Completo del Diplomado
                </h3>
                <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
                  Nuestro diplomado profesional en Bitcoin está diseñado para proporcionar una educación 
                  completa y práctica. Los tres módulos se complementan entre sí, construyendo desde 
                  fundamentos sólidos hasta aplicaciones avanzadas en el mundo real.
                </p>

                <div className="grid md:grid-cols-3 gap-8 pt-6">
                  <div className="space-y-2">
                    <div className="text-4xl font-bold text-orange-600 dark:text-orange-400">60+</div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Lecciones Totales</div>
                    <div className="text-xs text-muted-foreground">Contenido estructurado y progresivo</div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-4xl font-bold text-orange-600 dark:text-orange-400">36h</div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Horas de Contenido</div>
                    <div className="text-xs text-muted-foreground">Video, ejercicios y práctica</div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-4xl font-bold text-orange-600 dark:text-orange-400">100%</div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Gratis y Abierto</div>
                    <div className="text-xs text-muted-foreground">Acceso ilimitado para siempre</div>
                  </div>
                </div>

                <div className="pt-6">
                  <Link to="/lessons/beginner">
                    <Button size="lg" className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white shadow-lg h-14 px-10 text-lg">
                      <Book className="w-5 h-5 mr-2" />
                      Comenzar Módulo 1
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Progression Path */}
        <div className="mt-16">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">
              Tu Ruta de Progresión
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Sigue este camino estructurado para maximizar tu aprendizaje
            </p>
          </div>

          <div className="max-w-3xl mx-auto relative">
            {/* Connecting line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-orange-500 via-blue-500 to-purple-500 transform -translate-x-1/2 hidden md:block"></div>

            <div className="space-y-8">
              {learningPaths.map((path, index) => {
                const Icon = path.icon;
                const isEven = index % 2 === 0;
                
                return (
                  <div key={path.id} className={`flex items-center gap-8 ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    {/* Card */}
                    <div className="flex-1">
                      <Card className="border-2 hover:border-orange-200 dark:hover:border-orange-800 transition-all hover:shadow-lg">
                        <CardHeader>
                          <div className="flex items-center gap-3 mb-2">
                            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${path.color} flex items-center justify-center shadow-lg`}>
                              <Icon className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <div className="text-xs text-muted-foreground">Paso {index + 1}</div>
                              <CardTitle className="text-xl">{path.title}</CardTitle>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="flex gap-3 text-sm text-muted-foreground">
                            <span>{path.lessons} lecciones</span>
                            <span>•</span>
                            <span>{path.duration}</span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {path.description}
                          </p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Center dot */}
                    <div className="hidden md:flex items-center justify-center flex-shrink-0">
                      <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${path.color} shadow-lg flex items-center justify-center`}>
                        <span className="text-white font-bold text-sm">{index + 1}</span>
                      </div>
                    </div>

                    {/* Spacer */}
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <DonationSection />
      <Footer />
    </div>
  );
};

export default LearningPaths;
